import { Color, zIndex } from '@billy/design-system'
import { css } from '@emotion/core'
import styled from '@emotion/styled'
import { transparentize } from 'polished'

/*
  We need to support situations like this: (data-row-id="row-1", data-row-id="row-2")::before or .exclude-opacity
  where the suffix (pseudo-element or class) should be applied to each reviewed row.
  data-row-id="row-1", data-row-id="row-2"::before would apply ::before only to the last one
  so we need to generate separate selectors like data-row-id="row-1"::before, data-row-id="row-2"::before etc
*/
const generateSelectors = (reviewedRowIds: Set<string>, suffix: string): string => {
  return Array.from(reviewedRowIds)
    .map((id) => `[data-row-id="${id}"]${suffix}`)
    .join(', ')
}

export const generateReviewedRowStyles = (reviewedRowIds: Set<string>) => {
  if (reviewedRowIds.size === 0) {
    return css``
  }

  const selectors = generateSelectors(reviewedRowIds, '')
  const beforeSelectors = generateSelectors(reviewedRowIds, '::before')
  const excludeOpacitySelectors = generateSelectors(reviewedRowIds, ' .exclude-opacity')
  const excludeOpacityChildrenSelectors = generateSelectors(reviewedRowIds, ' .exclude-opacity *')
  const paragraphSelectors = generateSelectors(reviewedRowIds, ' p')

  return css`
    ${selectors} {
      position: relative;
      background-color: ${Color.Grey100};
    }

    /* Gray overlay effect using pseudo-element - simulate disabled effect */
    ${beforeSelectors} {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
      background-color: #fdfdfd3b;
      pointer-events: none;
    }

    ${paragraphSelectors} {
      color: ${Color.Grey500};
    }

    /* Bring checkbox and switch above the overlay - they are not disabled */
    ${excludeOpacitySelectors},
    ${excludeOpacityChildrenSelectors} {
      position: relative;
      z-index: 2;
    }
  `
}

interface TableWrapperProps {
  reviewedRowIds: Set<string>
}

export const TableWrapper = styled.div<TableWrapperProps>`
  ${({ reviewedRowIds }) => generateReviewedRowStyles(reviewedRowIds)}
`

export const BlurredOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${zIndex.ModalOverlay};
  background-color: ${transparentize(0.9, Color.White)};
  backdrop-filter: blur(2px);
  pointer-events: none;
`

interface ContentWrapperProps {
  hasUpsellScope: boolean
}

export const ContentWrapper = styled.div<ContentWrapperProps>`
  ${({ hasUpsellScope }) =>
    hasUpsellScope &&
    css`
      filter: blur(2px);
      pointer-events: none;
      user-select: none;
    `}
`
